'use client';

import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Menu, X, PlusCircle, Shield, FileText, Users, File, MessageSquare, CheckSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Link from 'next/link';
import Header from '@/components/Header';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useRouter, usePathname } from 'next/navigation';
import { getSessions, getSessionSummary, deleteSession } from '@/utils/apiClient';

// Define the session type based on your backend schema
interface WorkshopSession {
    id: string;
    name: string;
    status: 'live' | 'completed' | 'draft' | 'running' | 'Draft';
    role?: string;
}

const SessionCard = ({ session, user, onDelete }: { session: WorkshopSession, user: any, onDelete: (sessionId: string) => void }) => {
    const router = useRouter();
    const [summary, setSummary] = useState<string | null>(null);
    const [isSummaryOpen, setIsSummaryOpen] = useState(false);

    const parseSummary = (summaryText: string | null) => {
        if (!summaryText) return [];
        return summaryText.split('\n').filter(line => line.trim() !== '' && line.includes(':'));
    };

    const handleSummary = async () => {
        try {
            const summaryData = await getSessionSummary(session.id);
            setSummary(summaryData.summary);
            setIsSummaryOpen(true);
        } catch (error) {
            console.error('Failed to fetch session summary:', error);
            // Optionally, show an error message to the user
        }
    };

    const getStatusClasses = (status: string) => {
        switch (status) {
            case 'live': return 'bg-red-100 text-red-800';
            case 'completed': return 'bg-gray-100 text-gray-800';
            case 'draft': return 'bg-yellow-100 text-yellow-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const handleEdit = () => {
        router.push(`/sessions/${session.id}/edit`);
    };

    const handleDelete = async () => {
        if (window.confirm('Are you sure you want to delete this session?')) {
            try {
                await deleteSession(session.id);
                onDelete(session.id);
            } catch (error) {
                console.error('Failed to delete session:', error);
                alert('Failed to delete session.');
            }
        }
    };

    return (
        <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6 flex flex-col justify-between">
            <div>
                <div className="flex justify-between items-start">
                    <h3 className="text-lg font-bold text-gray-800">{session.name}</h3>
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusClasses(session.status)}`}>
                        {session.status}
                    </span>
                </div>
                <p className="text-sm text-gray-500 mt-2">Your role: {session.role}</p>
            </div>
            <div className="mt-6 flex flex-col gap-2">
                
                    <Link href={`/sessions/${session.id}/workspace`} passHref>
                        <Button className="w-full bg-brand-500 hover:bg-brand-600">Go to Workspace</Button>
                    </Link>
                <Button variant="outline" className="w-full" onClick={handleEdit}>Edit & Prepare</Button>
                <Button variant="outline" className="w-full" onClick={handleSummary}>
                    <FileText className="mr-2 h-4 w-4" />
                    Summary
                </Button>
                {user?.roles?.includes('moderator') && (
                    <Button variant="destructive" className="w-full" onClick={handleDelete}>
                        Delete
                    </Button>
                )}
            </div>

            {isSummaryOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4" onClick={() => setIsSummaryOpen(false)}>
                    <Card className="w-full max-w-md shadow-2xl" onClick={(e) => e.stopPropagation()}>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-3 text-2xl">
                                <FileText className="h-6 w-6 text-brand-600" />
                                Session Summary
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-4">
                                {parseSummary(summary).map((line, index) => {
                                    const [key, ...valueParts] = line.split(':');
                                    const value = valueParts.join(':').trim();
                                    let Icon;
                                    switch (key.trim()) {
                                        case '- Status': Icon = CheckSquare; break;
                                        case '- Participants': Icon = Users; break;
                                        case '- Documents': Icon = File; break;
                                        case '- Conversation Messages': Icon = MessageSquare; break;
                                        case '- Parties': Icon = Users; break;
                                        default: Icon = FileText;
                                    }
                                    return (
                                        <div key={index} className="flex items-start gap-3">
                                            <Icon className="h-5 w-5 text-gray-500 mt-1" />
                                            <div>
                                                <p className="font-semibold text-gray-800">{key.replace('-', '').trim()}</p>
                                                <p className="text-gray-600">{value}</p>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            <div className="mt-8 flex justify-end">
                                <Button onClick={() => setIsSummaryOpen(false)}>Close</Button>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}
        </div>
    );
};

export default function Dashboard() {
    const { user, isLoading: authLoading } = useAuth();
    const router = useRouter();
    const pathname = usePathname();
    const [open, setOpen] = useState(true);
    const [drawer, setDrawer] = useState(false);
    const [sessions, setSessions] = useState<WorkshopSession[]>([]);
    const [loading, setLoading] = useState(true);

    const handleDeleteSession = (sessionId: string) => {
        setSessions(sessions.filter(s => s.id !== sessionId));
    };

    useEffect(() => {
        const fetchSessions = async () => {
            if (!user) {
                setLoading(false);
                return;
            }
            try {
                const data = await getSessions();
                setSessions(data);
            } catch (error) {
                console.error('Failed to fetch sessions:', error);
            } finally {
                setLoading(false);
            }
        };

        if (!authLoading) {
            fetchSessions();
        }
    }, [user, authLoading]);

    const sidebarW = open ? 'w-64' : 'w-16';
    const toggleSidebar = () =>
        window.innerWidth < 768 ? setDrawer(true) : setOpen(!open);

    const NavLink = ({
        label,
        icon,
        active,
        href = '#',
    }: {
        label: string;
        icon: React.ReactNode;
        active?: boolean;
        href?: string;
    }) => (
        <Link href={href} className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
            active
                ? 'bg-brand-50 text-brand-600 font-semibold'
                : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
        }`}

>
            {icon}
            {open && <span className="whitespace-nowrap">{label}</span>}
        </Link>
    );

    if (authLoading || loading) {
        return (
            <div className="flex justify-center items-center min-h-screen">
                <p>Loading dashboard...</p>
            </div>
        );
    }

    return (
        <main className="min-h-screen flex bg-gray-50 text-gray-800">
            <div className="flex-1 flex flex-col">
                <Header toggleSidebar={toggleSidebar} />
                <section className="flex-1 p-8 bg-gray-100/50">
                    <div className="max-w-7xl mx-auto">
                        <div className="flex justify-between items-center mb-8">
                            <h1 className="text-3xl font-bold text-gray-900">Workshop Sessions</h1>
                            {user?.roles?.includes('moderator') && (
                                <Button size="lg" onClick={() => router.push('/sessions/create')}>
                                    <PlusCircle className="mr-2 h-5 w-5" />
                                    Create New Session
                                </Button>
                            )}
                        </div>

                        {user ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                                {sessions.map(session => (
                                    <SessionCard key={session.id} session={session} user={user} onDelete={handleDeleteSession} />
                                ))}
                            </div>
                        ) : (
                            <div className="text-center mt-16 bg-white p-12 rounded-lg shadow-md">
                                <h2 className="text-2xl font-semibold mb-4">Get Started</h2>
                                <p className="text-gray-600 mb-8 max-w-md mx-auto">
                                    Log in to access your role-specific dashboard and participate in the negotiation session.
                                </p>
                                <Link href="/login">
                                    <Button size="lg" className="bg-brand-500 hover:bg-brand-600">
                                        Login to Continue
                                    </Button>
                                </Link>
                            </div>
                        )}
                    </div>
                </section>

                <footer className="text-center text-sm text-gray-500 py-4 border-t bg-white">
                    © {new Date().getFullYear()} Negotiation Workshop App
                </footer>
            </div>
        </main>
    );
}
